import { useState, useEffect, useRef } from "react";
import { Headphones } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// ─── Animation constants ──────────────────────────────────────────────────────
const CANVAS_SIZE       = 2160;
const STROKE_DRAW_COLOR = "#fe5596";
const STROKE_DONE_COLOR = "#000000";
const BASE_PX_MS        = 3.0;

// ─── Countdown seconds per level ─────────────────────────────────────────────
const COUNTDOWN_SECS = { 1: 5, 2: 4, 3: 3 };

// ─── Translations ─────────────────────────────────────────────────────────────
const T = {
    en: {
        title:       'Play the Game "Which Kana Is This?"',
        subtitle:    "Listen and tap the kana you hear! Select a level below to start!",
        hiragana:    "Hiragana",
        katakana:    "Katakana",
        level:       "Level",
        easy:        "Start here",
        mixed:       "Try this when you're ready.",
        tricky:      "Give this a try if you're feeling confident!",
        daku:        "Voiced Kana (ga～zo)",
        whichChar:   "Which character is this?",
        tapToHear:   "Tap to listen again",
        replaysLeft: (n) => `You can replay ${n} more time${n !== 1 ? "s" : ""}`,
        perfect:     "Perfect!",
        great:       "Great job!",
        keepGoing:   "Keep going!",
        tryAgain:    "Try again!",
        review:      "Missed some kana? Tap to listen again!",
        noMistakes:  "Try a different kana!",
        playAgain:   "Play Again",
        backToMenu:  "Back to menu",
        goodjob:     "Good job!",
        outOf:       "/ 10",
        compoundKana:  "Compound Kana",
        compoundDesc:  "きゃ · しゃ · ちゃ · にゃ · ひゃ",
        compoundLabel: "Compound",
    },
    zh: {
        title:       "玩《这是哪个假名？》游戏",
        subtitle:    "点击你听到的假名！选择下面的难度开始吧！",
        hiragana:    "平假名",
        katakana:    "片假名",
        level:       "级别",
        easy:        "先从这里开始吧！",
        mixed:       "熟悉之后再来试试看吧！",
        tricky:      "如果有信心，就来挑战一下吧！",
        daku:        "浊音（が〜ぞ）",
        whichChar:   "这是哪个假名？",
        tapToHear:   "点击再听一次",
        replaysLeft: (n) => `你还可以重播 ${n} 次哦`,
        perfect:     "牛牛！",
        great:       "做得很好！",
        keepGoing:   "继续努力！",
        tryAgain:    "再试一次！",
        review:      "来复习一下答错的假名吧！点击再听一次哦",
        noMistakes:  "试试别的假名吧！",
        playAgain:   "再玩一次",
        backToMenu:  "返回菜单",
        goodjob:     "不错不错",
        outOf:       "/ 10",
        compoundKana:  "拗音",
        compoundDesc:  "きゃ · しゃ · ちゃ · にゃ · ひゃ",
        compoundLabel: "拗音",
    },
    jp: {
        title:       "ゲーム「どっちかな？」をあそぶ",
        subtitle:    "きこえたかなをタップしよう！したからレベルをえらんでさっそくはじめよう！",
        hiragana:    "ひらがな",
        katakana:    "カタカナ",
        level:       "レベル",
        easy:        "まずはここからはじめよう",
        mixed:       "なれてきたらためしてみて",
        tricky:      "じしんがあればやってみよう",
        daku:        "だくてん（が〜ぞ）",
        whichChar:   "どの文字かな？",
        tapToHear:   "タップしてもういっかいきく",
        replaysLeft: (n) => `あともう${n}回きけるよ`,
        perfect:     "ばっちり！",
        great:       "よくできました！",
        keepGoing:   "がんばって！",
        tryAgain:    "おしい！",
        review:      "まちがえたかなをふくしゅうしよう。タップしてもういっかいきいてね",
        noMistakes:  "ちがうかなでもやってみよう！",
        playAgain:   "もういっかい！",
        backToMenu:  "メニューにもどる",
        goodjob:     "よくできました",
        outOf:       "/ 10",
        compoundKana:  "ようおん",
        compoundDesc:  "きゃ · しゃ · ちゃ · にゃ · ひゃ",
        compoundLabel: "ようおん",
    },
};

// ─── Bundled SVG URLs ─────────────────────────────────────────────────────────
const SVG_ALL_URLS = {};
try {
    const req = require.context("../assets/svg", false, /\.svg$/i);
    req.keys().forEach((k) => {
        const m = k.match(/[/\\](hiragana|katakana)_all(2?)\.svg$/i);
        if (m) SVG_ALL_URLS[m[1].toLowerCase() + (m[2] || "")] = req(k);
    });
} catch (_) { }

// ─── Bundled video (hanamaru) ─────────────────────────────────────────────────
let HANAMARU_VIDEO = null;
try {
    const gifReq = require.context("../assets/gif", false, /\.(mp4|webm)$/i);
    gifReq.keys().forEach((k) => {
        if (/[/\\]hanamaru\.\w+$/i.test(k)) HANAMARU_VIDEO = gifReq(k);
    });
} catch (_) { }

// ─── Character data ───────────────────────────────────────────────────────────
const HIRAGANA = [
    { char: "あ", key: "a" }, { char: "い", key: "i" }, { char: "う", key: "u" }, { char: "え", key: "e" }, { char: "お", key: "o" },
    { char: "か", key: "ka" }, { char: "き", key: "ki" }, { char: "く", key: "ku" }, { char: "け", key: "ke" }, { char: "こ", key: "ko" },
    { char: "さ", key: "sa" }, { char: "し", key: "shi" }, { char: "す", key: "su" }, { char: "せ", key: "se" }, { char: "そ", key: "so" },
    { char: "た", key: "ta" }, { char: "ち", key: "chi" }, { char: "つ", key: "tsu" }, { char: "て", key: "te" }, { char: "と", key: "to" },
    { char: "な", key: "na" }, { char: "に", key: "ni" }, { char: "ぬ", key: "nu" }, { char: "ね", key: "ne" }, { char: "の", key: "no" },
    { char: "は", key: "ha" }, { char: "ひ", key: "hi" }, { char: "ふ", key: "fu" }, { char: "へ", key: "he" }, { char: "ほ", key: "ho" },
    { char: "ま", key: "ma" }, { char: "み", key: "mi" }, { char: "む", key: "mu" }, { char: "め", key: "me" }, { char: "も", key: "mo" },
    { char: "や", key: "ya" }, { char: "ゆ", key: "yu" }, { char: "よ", key: "yo" },
    { char: "ら", key: "ra" }, { char: "り", key: "ri" }, { char: "る", key: "ru" }, { char: "れ", key: "re" }, { char: "ろ", key: "ro" },
    { char: "わ", key: "wa" }, { char: "を", key: "wo", romaji: "o" }, { char: "ん", key: "n", romaji: "n(m)" },
    // Dakuten (voiced) が〜ぞ・だ〜ど・ば〜ぼ and handakuten ぱ〜ぽ
    { char: "が", key: "ga", daku: true }, { char: "ぎ", key: "gi", daku: true }, { char: "ぐ", key: "gu", daku: true }, { char: "げ", key: "ge", daku: true }, { char: "ご", key: "go", daku: true },
    { char: "ざ", key: "za", daku: true }, { char: "じ", key: "ji", daku: true }, { char: "ず", key: "zu", daku: true }, { char: "ぜ", key: "ze", daku: true }, { char: "ぞ", key: "zo", daku: true },
    { char: "だ", key: "da", daku: true }, { char: "ぢ", key: "di", daku: true }, { char: "づ", key: "du", daku: true }, { char: "で", key: "de", daku: true }, { char: "ど", key: "do", daku: true },
    { char: "ば", key: "ba", daku: true }, { char: "び", key: "bi", daku: true }, { char: "ぶ", key: "bu", daku: true }, { char: "べ", key: "be", daku: true }, { char: "ぼ", key: "bo", daku: true },
    { char: "ぱ", key: "pa", daku: true }, { char: "ぴ", key: "pi", daku: true }, { char: "ぷ", key: "pu", daku: true }, { char: "ぺ", key: "pe", daku: true }, { char: "ぽ", key: "po", daku: true },
    // Compound kana (拗音) きゃ〜ひょ
    { char: "きゃ", key: "kya", compound: true, parts: ["ki", "ya"] },
    { char: "きゅ", key: "kyu", compound: true, parts: ["ki", "yu"] },
    { char: "きょ", key: "kyo", compound: true, parts: ["ki", "yo"] },
    { char: "しゃ", key: "sha", compound: true, parts: ["shi", "ya"] },
    { char: "しゅ", key: "shu", compound: true, parts: ["shi", "yu"] },
    { char: "しょ", key: "sho", compound: true, parts: ["shi", "yo"] },
    { char: "ちゃ", key: "cha", compound: true, parts: ["chi", "ya"] },
    { char: "ちゅ", key: "chu", compound: true, parts: ["chi", "yu"] },
    { char: "ちょ", key: "cho", compound: true, parts: ["chi", "yo"] },
    { char: "にゃ", key: "nya", compound: true, parts: ["ni", "ya"] },
    { char: "にゅ", key: "nyu", compound: true, parts: ["ni", "yu"] },
    { char: "にょ", key: "nyo", compound: true, parts: ["ni", "yo"] },
    { char: "ひゃ", key: "hya", compound: true, parts: ["hi", "ya"] },
    { char: "ひゅ", key: "hyu", compound: true, parts: ["hi", "yu"] },
    { char: "ひょ", key: "hyo", compound: true, parts: ["hi", "yo"] },
    { char: "りゃ", key: "rya", compound: true, parts: ["ri", "ya"] },
    { char: "りゅ", key: "ryu", compound: true, parts: ["ri", "yu"] },
    { char: "りょ", key: "ryo", compound: true, parts: ["ri", "yo"] },
    { char: "みゃ", key: "mya", compound: true, parts: ["mi", "ya"] },
    { char: "みゅ", key: "myu", compound: true, parts: ["mi", "yu"] },
    { char: "みょ", key: "myo", compound: true, parts: ["mi", "yo"] },
    // Compound + dakuten/handakuten (拗濁音・拗半濁音) ぎゃ〜ぴょ
    { char: "ぎゃ", key: "gya", compound: true, daku: true, parts: ["gi", "ya"] },
    { char: "ぎゅ", key: "gyu", compound: true, daku: true, parts: ["gi", "yu"] },
    { char: "ぎょ", key: "gyo", compound: true, daku: true, parts: ["gi", "yo"] },
    { char: "じゃ", key: "ja",  compound: true, daku: true, parts: ["ji", "ya"] },
    { char: "じゅ", key: "ju",  compound: true, daku: true, parts: ["ji", "yu"] },
    { char: "じょ", key: "jo",  compound: true, daku: true, parts: ["ji", "yo"] },
    { char: "びゃ", key: "bya", compound: true, daku: true, parts: ["bi", "ya"] },
    { char: "びゅ", key: "byu", compound: true, daku: true, parts: ["bi", "yu"] },
    { char: "びょ", key: "byo", compound: true, daku: true, parts: ["bi", "yo"] },
    { char: "ぴゃ", key: "pya", compound: true, daku: true, parts: ["pi", "ya"] },
    { char: "ぴゅ", key: "pyu", compound: true, daku: true, parts: ["pi", "yu"] },
    { char: "ぴょ", key: "pyo", compound: true, daku: true, parts: ["pi", "yo"] },
];
const KATAKANA = [
    { char: "ア", key: "a" }, { char: "イ", key: "i" }, { char: "ウ", key: "u" }, { char: "エ", key: "e" }, { char: "オ", key: "o" },
    { char: "カ", key: "ka" }, { char: "キ", key: "ki" }, { char: "ク", key: "ku" }, { char: "ケ", key: "ke" }, { char: "コ", key: "ko" },
    { char: "サ", key: "sa" }, { char: "シ", key: "shi" }, { char: "ス", key: "su" }, { char: "セ", key: "se" }, { char: "ソ", key: "so" },
    { char: "タ", key: "ta" }, { char: "チ", key: "chi" }, { char: "ツ", key: "tsu" }, { char: "テ", key: "te" }, { char: "ト", key: "to" },
    { char: "ナ", key: "na" }, { char: "ニ", key: "ni" }, { char: "ヌ", key: "nu" }, { char: "ネ", key: "ne" }, { char: "ノ", key: "no" },
    { char: "ハ", key: "ha" }, { char: "ヒ", key: "hi" }, { char: "フ", key: "fu" }, { char: "ヘ", key: "he" }, { char: "ホ", key: "ho" },
    { char: "マ", key: "ma" }, { char: "ミ", key: "mi" }, { char: "ム", key: "mu" }, { char: "メ", key: "me" }, { char: "モ", key: "mo" },
    { char: "ヤ", key: "ya" }, { char: "ユ", key: "yu" }, { char: "ヨ", key: "yo" },
    { char: "ラ", key: "ra" }, { char: "リ", key: "ri" }, { char: "ル", key: "ru" }, { char: "レ", key: "re" }, { char: "ロ", key: "ro" },
    { char: "ワ", key: "wa" }, { char: "ヲ", key: "wo", romaji: "o" }, { char: "ン", key: "n", romaji: "n(m)" },
    // Dakuten (voiced) ガ〜ゾ・ダ〜ド・バ〜ボ and handakuten パ〜ポ
    { char: "ガ", key: "ga", daku: true }, { char: "ギ", key: "gi", daku: true }, { char: "グ", key: "gu", daku: true }, { char: "ゲ", key: "ge", daku: true }, { char: "ゴ", key: "go", daku: true },
    { char: "ザ", key: "za", daku: true }, { char: "ジ", key: "ji", daku: true }, { char: "ズ", key: "zu", daku: true }, { char: "ゼ", key: "ze", daku: true }, { char: "ゾ", key: "zo", daku: true },
    { char: "ダ", key: "da", daku: true }, { char: "ヂ", key: "di", daku: true }, { char: "ヅ", key: "du", daku: true }, { char: "デ", key: "de", daku: true }, { char: "ド", key: "do", daku: true },
    { char: "バ", key: "ba", daku: true }, { char: "ビ", key: "bi", daku: true }, { char: "ブ", key: "bu", daku: true }, { char: "ベ", key: "be", daku: true }, { char: "ボ", key: "bo", daku: true },
    { char: "パ", key: "pa", daku: true }, { char: "ピ", key: "pi", daku: true }, { char: "プ", key: "pu", daku: true }, { char: "ペ", key: "pe", daku: true }, { char: "ポ", key: "po", daku: true },
    // Compound kana (拗音) キャ〜ヒョ
    { char: "キャ", key: "kya", compound: true, parts: ["ki", "ya"] },
    { char: "キュ", key: "kyu", compound: true, parts: ["ki", "yu"] },
    { char: "キョ", key: "kyo", compound: true, parts: ["ki", "yo"] },
    { char: "シャ", key: "sha", compound: true, parts: ["shi", "ya"] },
    { char: "シュ", key: "shu", compound: true, parts: ["shi", "yu"] },
    { char: "ショ", key: "sho", compound: true, parts: ["shi", "yo"] },
    { char: "チャ", key: "cha", compound: true, parts: ["chi", "ya"] },
    { char: "チュ", key: "chu", compound: true, parts: ["chi", "yu"] },
    { char: "チョ", key: "cho", compound: true, parts: ["chi", "yo"] },
    { char: "ニャ", key: "nya", compound: true, parts: ["ni", "ya"] },
    { char: "ニュ", key: "nyu", compound: true, parts: ["ni", "yu"] },
    { char: "ニョ", key: "nyo", compound: true, parts: ["ni", "yo"] },
    { char: "ヒャ", key: "hya", compound: true, parts: ["hi", "ya"] },
    { char: "ヒュ", key: "hyu", compound: true, parts: ["hi", "yu"] },
    { char: "ヒョ", key: "hyo", compound: true, parts: ["hi", "yo"] },
    { char: "リャ", key: "rya", compound: true, parts: ["ri", "ya"] },
    { char: "リュ", key: "ryu", compound: true, parts: ["ri", "yu"] },
    { char: "リョ", key: "ryo", compound: true, parts: ["ri", "yo"] },
    { char: "ミャ", key: "mya", compound: true, parts: ["mi", "ya"] },
    { char: "ミュ", key: "myu", compound: true, parts: ["mi", "yu"] },
    { char: "ミョ", key: "myo", compound: true, parts: ["mi", "yo"] },
    // Compound + dakuten/handakuten (拗濁音・拗半濁音) ギャ〜ピョ
    { char: "ギャ", key: "gya", compound: true, daku: true, parts: ["gi", "ya"] },
    { char: "ギュ", key: "gyu", compound: true, daku: true, parts: ["gi", "yu"] },
    { char: "ギョ", key: "gyo", compound: true, daku: true, parts: ["gi", "yo"] },
    { char: "ジャ", key: "ja",  compound: true, daku: true, parts: ["ji", "ya"] },
    { char: "ジュ", key: "ju",  compound: true, daku: true, parts: ["ji", "yu"] },
    { char: "ジョ", key: "jo",  compound: true, daku: true, parts: ["ji", "yo"] },
    { char: "ビャ", key: "bya", compound: true, daku: true, parts: ["bi", "ya"] },
    { char: "ビュ", key: "byu", compound: true, daku: true, parts: ["bi", "yu"] },
    { char: "ビョ", key: "byo", compound: true, daku: true, parts: ["bi", "yo"] },
    { char: "ピャ", key: "pya", compound: true, daku: true, parts: ["pi", "ya"] },
    { char: "ピュ", key: "pyu", compound: true, daku: true, parts: ["pi", "yu"] },
    { char: "ピョ", key: "pyo", compound: true, daku: true, parts: ["pi", "yo"] },
];

// ─── Pairs never to quiz (confusable but excluded by design) ─────────────────
const NEVER_PAIR_H = [
    ["を", "お"],   // wo/o — same sound, too confusing
];
const NEVER_PAIR_K = [
    ["ヲ", "オ"],   // wo/o
];

// ─── Level 2 confusable pairs ─────────────────────────────────────────────────
const LEVEL2_H = [
    ["き", "さ"], ["さ", "ち"], ["ぬ", "め"], ["ね", "わ"], ["れ", "わ"],
    ["は", "ほ"], ["る", "ろ"], ["ま", "ほ"], ["あ", "め"], ["の", "め"],
    ["む", "す"],
];
const LEVEL2_K = [
    ["シ", "ツ"], ["ソ", "ン"], ["ク", "ケ"], ["コ", "ユ"], ["フ", "ワ"],
    ["ウ", "ワ"], ["リ", "イ"], ["ナ", "メ"], ["ヌ", "ス"], ["チ", "テ"],
];

// ─── Level 3 compound/yōon + dakuten compound + da～po + ga～zo pairs ──────────
const LEVEL3_H = [
    // compound kana confusables
    ["しゃ", "りょ"], ["しゅ", "ちゅ"], ["しょ", "ちょ"],
    ["りゃ", "りゅ"], ["りゃ", "りょ"], ["りゅ", "りょ"],
    ["きゃ", "か"], ["きゅ", "く"], ["きょ", "こ"],
    ["しゃ", "さ"], ["しゅ", "す"], ["しょ", "そ"],
    ["ちゃ", "た"], ["ちゅ", "つ"], ["ちょ", "と"],
    ["にゃ", "な"], ["ひゃ", "は"], ["みゃ", "ま"], ["りゃ", "ら"],
    // voiced/handakuten compound kana (ぎゃ〜ぴょ) vs unvoiced counterparts
    ["ぎゃ", "きゃ"], ["ぎゅ", "きゅ"], ["ぎょ", "きょ"],
    ["じゃ", "しゃ"], ["じゅ", "しゅ"], ["じょ", "しょ"],
    ["びゃ", "ひゃ"], ["びゅ", "ひゅ"], ["びょ", "ひょ"],
    ["ぴゃ", "びゃ"], ["ぴゅ", "びゅ"], ["ぴょ", "びょ"],
    // da～po voiced vs unvoiced
    ["だ", "た"], ["で", "て"], ["ど", "と"],
    ["ば", "は"], ["び", "ひ"], ["べ", "へ"], ["ぼ", "ほ"],
    // handakuten vs dakuten (pa vs ba)
    ["ぱ", "ば"], ["ぴ", "び"], ["ぷ", "ぶ"], ["ぺ", "べ"], ["ぽ", "ぼ"],
    // ga～zo voiced vs unvoiced
    ["が", "か"], ["ぎ", "き"], ["ぐ", "く"], ["げ", "け"], ["ご", "こ"],
    ["ざ", "さ"], ["じ", "し"], ["ぜ", "せ"], ["ぞ", "そ"],
    // easily confused
    ["が", "ご"], ["ざ", "ぞ"], ["ぎ", "ぐ"],
];
const LEVEL3_K = [
    // compound kana confusables
    ["シャ", "リョ"], ["シュ", "チュ"], ["ショ", "チョ"],
    ["リャ", "リュ"], ["リャ", "リョ"], ["リュ", "リョ"],
    ["キャ", "カ"], ["キュ", "ク"], ["キョ", "コ"],
    ["シャ", "サ"], ["シュ", "ス"], ["ショ", "ソ"],
    ["チャ", "タ"], ["チュ", "ツ"], ["チョ", "ト"],
    ["ニャ", "ナ"], ["ヒャ", "ハ"], ["ミャ", "マ"], ["リャ", "ラ"],
    // voiced/handakuten compound kana (ギャ〜ピョ)
    ["ギャ", "キャ"], ["ギュ", "キュ"], ["ギョ", "キョ"],
    ["ジャ", "シャ"], ["ジュ", "シュ"], ["ジョ", "ショ"],
    ["ビャ", "ヒャ"], ["ビュ", "ヒュ"], ["ビョ", "ヒョ"],
    ["ピャ", "ビャ"], ["ピュ", "ビュ"], ["ピョ", "ビョ"],
    // da～po voiced vs unvoiced
    ["ダ", "タ"], ["デ", "テ"], ["ド", "ト"],
    ["バ", "ハ"], ["ビ", "ヒ"], ["ベ", "ヘ"], ["ボ", "ホ"],
    // handakuten vs dakuten
    ["パ", "バ"], ["ピ", "ビ"], ["プ", "ブ"], ["ペ", "ベ"], ["ポ", "ボ"],
    // ga～zo voiced vs unvoiced
    ["ガ", "カ"], ["ギ", "キ"], ["グ", "ク"], ["ゲ", "ケ"], ["ゴ", "コ"],
    ["ザ", "サ"], ["ジ", "シ"], ["ゼ", "セ"], ["ゾ", "ソ"],
    ["ガ", "ゴ"], ["ザ", "ゾ"], ["ギ", "グ"],
];

// ─── Compound kana pairs (for the compound-only quiz mode) ────────────────────
// Each pair is [compound, other] — the compound kana is ALWAYS the answer/audio.
// "other" is either the base kana (ちゃ vs ち) or another compound (ちゃ vs きゃ).
const COMPOUND_PAIRS_H = [
    // compound vs its base kana
    ["きゃ","き"],["きゅ","き"],["きょ","き"],
    ["しゃ","し"],["しゅ","し"],["しょ","し"],
    ["ちゃ","ち"],["ちゅ","ち"],["ちょ","ち"],
    ["にゃ","に"],["にゅ","に"],["にょ","に"],
    ["ひゃ","ひ"],["ひゅ","ひ"],["ひょ","ひ"],
    // compound vs different compound (same vowel row)
    ["きゃ","しゃ"],["きゅ","しゅ"],["きょ","しょ"],
    ["しゃ","ちゃ"],["しゅ","ちゅ"],["しょ","ちょ"],
    ["ちゃ","にゃ"],["ちゅ","にゅ"],["ちょ","にょ"],
    ["にゃ","ひゃ"],["にゅ","ひゅ"],["にょ","ひょ"],
    ["きゃ","ちゃ"],["きゅ","にゅ"],["きょ","ひょ"],
    ["しゃ","にゃ"],["しゅ","ひゅ"],["しょ","きょ"],
];
const COMPOUND_PAIRS_K = [
    ["キャ","キ"],["キュ","キ"],["キョ","キ"],
    ["シャ","シ"],["シュ","シ"],["ショ","シ"],
    ["チャ","チ"],["チュ","チ"],["チョ","チ"],
    ["ニャ","ニ"],["ニュ","ニ"],["ニョ","ニ"],
    ["ヒャ","ヒ"],["ヒュ","ヒ"],["ヒョ","ヒ"],
    ["キャ","シャ"],["キュ","シュ"],["キョ","ショ"],
    ["シャ","チャ"],["シュ","チュ"],["ショ","チョ"],
    ["チャ","ニャ"],["チュ","ニュ"],["チョ","ニョ"],
    ["ニャ","ヒャ"],["ニュ","ヒュ"],["ニョ","ヒョ"],
    ["キャ","チャ"],["キュ","ニュ"],["キョ","ヒョ"],
    ["シャ","ニャ"],["シュ","ヒュ"],["ショ","キョ"],
];

// ─── Audio ────────────────────────────────────────────────────────────────────
// Expected audio file names in src/assets/audio/:
//   Character sounds  : a.mp3, ka.mp3, shi.mp3 … (romaji key)
//   correct.mp3/m4a   : correct answer feedback
//   wrong.mp3/m4a     : wrong answer / timeout feedback
//   tap.mp3/m4a       : UI button tap
//   counting.mp3/m4a  : tick played each countdown second (played at > 3s left)
//   countdown.mp3/m4a : 3-second countdown audio, played when 3s left
//   hiraganaquiz_bgm.mp3/m4a : looping quiz background music
//   quizresult_bgm.mp3/m4a   : looping result screen background music
//   goodjob_en.m4a    : "Good job!" (English)
//   goodjob_zh.m4a    : "不错不错" (Chinese)
//   goodjob_jp.m4a    : "よくできました" (Japanese)
const CHAR_AUDIO    = {};
const FEEDBACK      = { correct: null, wrong: null };
const GOODJOB_AUDIO = { en: null, zh: null, jp: null };
let QUIZ_BGM        = null;
let RESULT_BGM      = null;
let TAP_AUDIO       = null;
let COUNTDOWN_END   = null;   // 3-second countdown played at 3s remaining
let COUNTING_AUDIO  = null;   // per-second tick (only when > 3s left)

try {
    const req = require.context("../assets/audio", false, /\.(mp3|wav|ogg|m4a)$/i);
    req.keys().forEach((k) => {
        if (/[/\\](hiragana|katakana)_/i.test(k)) return;

        // Named special files — checked before generic char audio
        if (/[/\\]hiraganaquiz_bgm\.\w+$/i.test(k)) { QUIZ_BGM = req(k);        return; }
        if (/[/\\]quizresult_bgm\.\w+$/i.test(k))   { RESULT_BGM = req(k);      return; }
        if (/[/\\]tap\.\w+$/i.test(k))               { TAP_AUDIO = req(k);       return; }
        if (/[/\\]countdown\.\w+$/i.test(k))         { COUNTDOWN_END = req(k);   return; }
        if (/[/\\](counting|tick)\.\w+$/i.test(k))   { COUNTING_AUDIO = req(k);  return; }

        const mFb = k.match(/[/\\](correct|wrong)\.\w+$/i);
        if (mFb) { FEEDBACK[mFb[1].toLowerCase()] = req(k); return; }

        const mGj = k.match(/[/\\]goodjob_(en|zh|jp)\.\w+$/i);
        if (mGj) { GOODJOB_AUDIO[mGj[1].toLowerCase()] = req(k); return; }
        // Also match "goodjob.m4a" or "good job.m4a" (space in filename)
        const mGj2 = k.match(/[/\\]good[\s_]?job[^/\\]*\.\w+$/i);
        if (mGj2 && !GOODJOB_AUDIO.en) { GOODJOB_AUDIO.en = req(k); return; }

        // Generic character audio — prefer .m4a
        const m = k.match(/[/\\]([a-z]+)\.\w+$/i);
        if (m) {
            const key = m[1].toLowerCase();
            if (!CHAR_AUDIO[key] || k.toLowerCase().endsWith(".m4a")) CHAR_AUDIO[key] = req(k);
        }
    });
} catch (_) { }

// ─── SVG stroke parsing + cache ───────────────────────────────────────────────
const _strokeCache = {};
const _fetchCache  = {};

function parseSVGStrokes(svgText, charKey) {
    const container = document.createElement("div");
    container.style.cssText = "position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;visibility:hidden;";
    container.innerHTML = svgText;
    document.body.appendChild(container);
    const strokes = [];
    try {
        const svgEl = container.querySelector("svg");
        if (!svgEl) return [];
        const vb = (svgEl.getAttribute("viewBox") || `0 0 ${CANVAS_SIZE} ${CANVAS_SIZE}`).split(/[\s,]+/).map(Number);
        const scaleX = CANVAS_SIZE / (vb[2] || CANVAS_SIZE);
        const scaleY = CANVAS_SIZE / (vb[3] || CANVAS_SIZE);
        let defaultLW = 130;
        const swM = (svgEl.querySelector("style")?.textContent || "").match(/stroke-width\s*:\s*([\d.]+)/);
        if (swM) defaultLW = parseFloat(swM[1]) * ((scaleX + scaleY) / 2);
        const searchRoot = charKey
            ? (svgEl.querySelector(`g[id="${charKey}"]`) ||
               svgEl.querySelector(`g[id="${charKey.replace(/_/g, "_x5F_")}"]`) ||
               svgEl.querySelector(`g[id^="${charKey}_"]`))
            : svgEl;
        if (!searchRoot) return [];
        const overrideStyle = document.createElementNS("http://www.w3.org/2000/svg", "style");
        overrideStyle.textContent = "* { display: block !important; }";
        svgEl.appendChild(overrideStyle);
        searchRoot.querySelectorAll("g[id^='stroke']").forEach((g) => {
            const strokeNum = parseInt(g.id.replace(/^stroke_(?:x5F_)?/i, ""), 10);
            const shape = g.querySelector("path, polyline, line, polygon");
            if (!shape) return;
            try {
                const totalLen = shape.getTotalLength();
                if (totalLen === 0) return;
                const pts = [];
                for (let i = 0; i <= 150; i++) {
                    const pt = shape.getPointAtLength((i / 150) * totalLen);
                    pts.push({ x: pt.x * scaleX, y: pt.y * scaleY });
                }
                let length = 0;
                for (let i = 1; i < pts.length; i++) {
                    const dx = pts[i].x - pts[i - 1].x, dy = pts[i].y - pts[i - 1].y;
                    length += Math.sqrt(dx * dx + dy * dy);
                }
                strokes.push({ strokeNum, pts, lineWidth: defaultLW, length });
            } catch (_) { }
        });
        strokes.sort((a, b) => a.strokeNum - b.strokeNum);
        svgEl.removeChild(overrideStyle);
    } finally { document.body.removeChild(container); }
    return strokes;
}

async function loadSVGStrokes(script, charKey) {
    const url = SVG_ALL_URLS[script];
    if (!url) throw new Error(`No bundled SVG for: ${script}`);
    const cacheKey = `${url}::${charKey}`;
    if (_strokeCache[cacheKey] !== undefined) return _strokeCache[cacheKey];
    if (!_fetchCache[url]) {
        _fetchCache[url] = fetch(url).then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); });
    }
    const text = await _fetchCache[url];
    if (_strokeCache[cacheKey] !== undefined) return _strokeCache[cacheKey];
    const strokes = parseSVGStrokes(text, charKey);
    // If not found in primary SVG, try secondary (e.g. hiragana2 for dakuten/handakuten)
    if (!strokes.length) {
        const url2 = SVG_ALL_URLS[script + "2"];
        if (url2) {
            const cacheKey2 = `${url2}::${charKey}`;
            if (_strokeCache[cacheKey2] !== undefined) return _strokeCache[cacheKey2];
            if (!_fetchCache[url2]) {
                _fetchCache[url2] = fetch(url2).then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); });
            }
            const text2 = await _fetchCache[url2];
            if (_strokeCache[cacheKey2] !== undefined) return _strokeCache[cacheKey2];
            const strokes2 = parseSVGStrokes(text2, charKey);
            _strokeCache[cacheKey2] = strokes2;
            return strokes2;
        }
    }
    _strokeCache[cacheKey] = strokes;
    return strokes;
}

function clearCanvas(ctx) { ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE); }

function drawStroke(ctx, stroke, progress = 1, color = STROKE_DONE_COLOR) {
    const { pts, lineWidth, length: totalLen } = stroke;
    if (!pts || !pts.length || !totalLen) return;
    const target = totalLen * Math.min(Math.max(progress, 0), 1);
    ctx.save(); ctx.strokeStyle = color; ctx.lineWidth = lineWidth || 130;
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
    let drawn = 0;
    for (let i = 1; i < pts.length; i++) {
        const dx = pts[i].x - pts[i - 1].x, dy = pts[i].y - pts[i - 1].y;
        const segLen = Math.sqrt(dx * dx + dy * dy);
        if (drawn + segLen >= target) {
            const t = segLen === 0 ? 0 : (target - drawn) / segLen;
            ctx.lineTo(pts[i - 1].x + dx * t, pts[i - 1].y + dy * t); break;
        }
        ctx.lineTo(pts[i].x, pts[i].y); drawn += segLen;
    }
    ctx.stroke(); ctx.restore();
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}
function pairKey(pair) { return [...pair].sort().join("|"); }

function generateQuestions(script, level, skipKeys = null) {
    const chars   = script === "hiragana" ? HIRAGANA : KATAKANA;
    const l2Pairs = script === "hiragana" ? LEVEL2_H : LEVEL2_K;
    const l3Pairs = script === "hiragana" ? LEVEL3_H : LEVEL3_K;
    const charMap = Object.fromEntries(chars.map((c) => [c.char, c]));
    const neverPairs = script === "hiragana" ? NEVER_PAIR_H : NEVER_PAIR_K;
    const l2Set   = new Set([
        ...l2Pairs.flatMap((p) => [`${p[0]}|${p[1]}`, `${p[1]}|${p[0]}`]),
        ...neverPairs.flatMap((p) => [`${p[0]}|${p[1]}`, `${p[1]}|${p[0]}`]),
    ]);
    // Randomly assign which character occupies slot 0 vs slot 1
    const makeRaw = ([a, b]) => {
        const swap = Math.random() < 0.5;
        const c0 = swap ? b : a, c1 = swap ? a : b;
        return { chars: [charMap[c0], charMap[c1]], pair: [c0, c1] };
    };
    const l2Pool  = shuffle([...l2Pairs]).map(makeRaw);
    const l3Pool  = shuffle([...l3Pairs]).map(makeRaw);
    const l1Pool  = (() => {
        // Level 1: only simple (non-compound, non-dakuten) chars, excluding Level 2 pairs
        const simpleChars = chars.filter(c => !c.compound && !c.daku);
        const cands = [];
        for (let i = 0; i < simpleChars.length; i++)
            for (let j = i + 1; j < simpleChars.length; j++)
                if (!l2Set.has(`${simpleChars[i].char}|${simpleChars[j].char}`))
                    cands.push([simpleChars[i].char, simpleChars[j].char]);
        return shuffle(cands).map(makeRaw);
    })();
    let pool = level === 1 ? l1Pool : level === 2 ? l2Pool : l3Pool;
    if (skipKeys && skipKeys.size > 0) {
        const kept = pool.filter((q) => !skipKeys.has(pairKey(q.pair)));
        const skipped = pool.filter((q) => skipKeys.has(pairKey(q.pair)));
        pool = kept.length >= 10 ? kept : [...kept, ...shuffle(skipped)];
    }
    pool = pool.slice(0, 10);
    const used = new Set();
    return pool.map((q) => {
        const [a, b] = q.pair;
        const aFree = !used.has(a), bFree = !used.has(b);
        let answerIdx = (aFree && bFree) ? (Math.random() < 0.5 ? 0 : 1)
            : aFree ? 0 : bFree ? 1 : (Math.random() < 0.5 ? 0 : 1);
        used.add(q.pair[answerIdx]);
        return { ...q, answerIdx };
    });
}

function generateCompoundQuestions(script) {
    const chars = script === "hiragana" ? HIRAGANA : KATAKANA;
    const charMap = Object.fromEntries(chars.map(c => [c.char, c]));
    const rawPairs = script === "hiragana" ? COMPOUND_PAIRS_H : COMPOUND_PAIRS_K;
    const picked = shuffle([...rawPairs]).slice(0, 10);
    return picked.map(([compound, other]) => {
        // The compound kana is always the answer (audio plays it)
        const swap = Math.random() < 0.5;
        const c0 = charMap[swap ? other : compound];
        const c1 = charMap[swap ? compound : other];
        const answerIdx = swap ? 1 : 0;
        return { chars: [c0, c1], pair: [compound, other], answerIdx };
    });
}

// ─── StrokeCanvas ─────────────────────────────────────────────────────────────
function StrokeCanvas({ script, charKey, animate = true, strokeColor = STROKE_DONE_COLOR, finishNow = false, onFailed, onFinished }) {
    const canvasRef    = useRef(null);
    const animRef      = useRef({ canceled: false, rafId: null });
    const finishNowRef = useRef(finishNow);
    useEffect(() => { finishNowRef.current = finishNow; }, [finishNow]);

    useEffect(() => {
        const anim = { canceled: false, rafId: null };
        animRef.current = anim;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        clearCanvas(ctx);

        loadSVGStrokes(script, charKey).then((strokes) => {
            if (anim.canceled) return;
            if (!strokes.length) { onFailed?.(); return; }
            if (!animate) {
                clearCanvas(ctx);
                strokes.forEach((s) => drawStroke(ctx, s, 1, strokeColor));
                onFinished?.();
                return;
            }
            let sIdx = 0, segStart = null;
            const drawAllDone = () => { clearCanvas(ctx); strokes.forEach((s) => drawStroke(ctx, s, 1, STROKE_DONE_COLOR)); onFinished?.(); };
            function frame(ts) {
                if (anim.canceled) return;
                if (finishNowRef.current) { drawAllDone(); return; }
                if (segStart === null) segStart = ts;
                const stroke   = strokes[sIdx];
                const progress = Math.min((ts - segStart) / (stroke.length / BASE_PX_MS), 1);
                clearCanvas(ctx);
                for (let i = 0; i < sIdx; i++) drawStroke(ctx, strokes[i], 1, STROKE_DONE_COLOR);
                drawStroke(ctx, stroke, progress, progress < 1 ? STROKE_DRAW_COLOR : STROKE_DONE_COLOR);
                if (progress >= 1) {
                    sIdx++;
                    if (sIdx >= strokes.length) { drawAllDone(); return; }
                    segStart = null;
                }
                anim.rafId = requestAnimationFrame(frame);
            }
            anim.rafId = requestAnimationFrame(frame);
        }).catch(() => { if (!anim.canceled) onFailed?.(); });

        return () => { anim.canceled = true; if (anim.rafId) cancelAnimationFrame(anim.rafId); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [script, charKey, animate, strokeColor]);

    return <canvas ref={canvasRef} width={CANVAS_SIZE} height={CANVAS_SIZE}
        style={{ display: "block", width: "100%", height: "100%" }} />;
}

// ─── HanamaruCanvas — chroma-key video player ─────────────────────────────────
// Renders hanamaru.mp4 on a canvas with the green background removed.
// `play` prop: when false the video is loaded but not started; set true to begin.
function HanamaruCanvas({ videoSrc, play = true }) {
    const videoRef  = useRef(null);
    const canvasRef = useRef(null);
    const rafRef    = useRef(null);

    useEffect(() => {
        const video  = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });

        const processFrame = () => {
            if (video.readyState >= 2 && !video.paused && !video.ended) {
                const w = canvas.width, h = canvas.height;
                ctx.drawImage(video, 0, 0, w, h);
                const img = ctx.getImageData(0, 0, w, h);
                const d   = img.data;
                for (let i = 0; i < d.length; i += 4) {
                    const r = d[i], g = d[i + 1], b = d[i + 2];
                    // Remove green-screen pixels — more permissive threshold for mobile
                    if (g > 80 && g > r * 1.15 && g > b * 1.1) d[i + 3] = 0;
                }
                ctx.putImageData(img, 0, 0);
            }
            rafRef.current = requestAnimationFrame(processFrame);
        };

        const startLoop = () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(processFrame);
        };

        const onMeta = () => {
            if (video.videoWidth) {
                canvas.width  = video.videoWidth;
                canvas.height = video.videoHeight;
            }
        };

        const onEnded = () => {
            if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
        };
        video.addEventListener("loadedmetadata", onMeta);
        video.addEventListener("play", startLoop);
        video.addEventListener("ended", onEnded);

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            video.removeEventListener("loadedmetadata", onMeta);
            video.removeEventListener("play", startLoop);
            video.removeEventListener("ended", onEnded);
            video.pause();
        };
    }, [videoSrc]);

    // Start or pause based on `play` prop
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        if (play) {
            video.play().catch(() => {});
        } else {
            video.pause();
        }
    }, [play]);

    return (
        <div className="relative flex justify-center items-center my-2">
            {/* Hidden video — kept tiny so iOS renders it but doesn't show it */}
            <video ref={videoRef} src={videoSrc} playsInline
                style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }} />
            {/* Canvas shows chroma-keyed frames on top of page background */}
            <canvas ref={canvasRef} width={480} height={480}
                style={{ maxWidth: "min(300px, 80vw)", height: "auto", display: "block", background: "transparent" }} />
        </div>
    );
}

// ─── Countdown ring ───────────────────────────────────────────────────────────
// Starts FULL and drains. key={qIdx} on usage ensures fresh mount per question.
function CountdownRing({ timeLeft, total }) {
    const hasAnimated = useRef(false);
    useEffect(() => { hasAnimated.current = true; }); // true from 2nd render onward

    const pct    = total > 0 ? timeLeft / total : 1;
    const r      = 13;
    const circ   = 2 * Math.PI * r;
    const urgent = timeLeft <= 2;

    return (
        <div className="relative flex-shrink-0" style={{ width: 36, height: 36 }}>
            <svg width="36" height="36" viewBox="0 0 36 36" style={{ transform: "rotate(-90deg)" }}>
                <circle cx="18" cy="18" r={r} fill="none" stroke="#e5e7eb" strokeWidth="3" />
                <circle cx="18" cy="18" r={r} fill="none"
                    stroke={urgent ? "#ef4444" : "#6b7280"}
                    strokeWidth="3"
                    strokeDasharray={circ}
                    strokeDashoffset={circ * (1 - pct)}
                    style={{ transition: hasAnimated.current ? "stroke-dashoffset 0.92s linear, stroke 0.3s" : "none" }}
                />
            </svg>
            <span className={`absolute inset-0 flex items-center justify-center text-[11px] font-bold
                ${urgent ? "text-[red-500]" : "text-[#6b7280]"}`}>
                {timeLeft}
            </span>
        </div>
    );
}

// ─── CompoundKanaLayout — two stroke canvases side by side ───────────────────
// Renders the main character full-size and the small kana (ya/yu/yo) at ~58%
// width with an underline at the bottom to mark it as a small kana.
function CompoundKanaLayout({ parts, script, animate, finishNow, onFailed, strokeColor, onFinished }) {
    const [smallStarted, setSmallStarted] = useState(false);

    // Reset sequential state on each new question (parts/script change)
    const partsKey = parts.join("|") + script;
    const partsKeyRef = useRef(partsKey);
    if (partsKeyRef.current !== partsKey) {
        partsKeyRef.current = partsKey;
        setSmallStarted(false);
    }

    // When finishNow fires (answered / timeout), skip straight to showing small kana
    useEffect(() => {
        if (finishNow) setSmallStarted(true);
    }, [finishNow]);

    return (
        // Outer centers the whole compound glyph in the button (H + V).
        // Inner bottom-aligns the two chars relative to each other.
        // 88% inner width → main ≈55% of button, small ≈33% of button.
        <div className="w-full h-full flex items-center justify-center">
            <div className="flex items-end" style={{ width: "88%" }}>
                {/* Main character — 62.5% of inner (≈55% of button) */}
                <div style={{ width: "62.5%", aspectRatio: "1 / 1" }}>
                    <StrokeCanvas script={script} charKey={parts[0]}
                        animate={animate} strokeColor={strokeColor}
                        finishNow={finishNow} onFailed={onFailed}
                        onFinished={() => setSmallStarted(true)} />
                </div>
                {/* Small kana — 37.5% of inner (≈33% of button), blank until main done */}
                <div style={{ width: "37.5%", aspectRatio: "1 / 1" }}>
                    {smallStarted && (
                        <StrokeCanvas script={script} charKey={parts[1]}
                            animate={animate} strokeColor={strokeColor}
                            finishNow={finishNow} onFailed={onFailed}
                            onFinished={onFinished} />
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── CharButton ───────────────────────────────────────────────────────────────
function CharButton({ char, script, isCorrect, answered, isSelected, onAnswer, onAnimationFinished, sizedAsCompound }) {
    const [failed, setFailed] = useState(false);
    const border = answered
        ? isCorrect ? "border-green-500 bg-green-50"
            : isSelected ? "border-red-500 bg-red-50"
            : "border-gray-200 bg-white"
        : "border-[#fdcb6e]/70 bg-white hover:border-[#2797c4] hover:border-[3px]";

    // When this plain kana is paired with a compound, render at ~55% width, centered,
    // matching the size of the main letter in the compound button.
    const plainKana = failed
        ? <span className="text-[clamp(2rem,8vmin,5rem)] font-bold text-gray-700 select-none leading-none">{char.char}</span>
        : sizedAsCompound
            ? <div className="w-full h-full flex items-center justify-center">
                <div style={{ width: "55%", aspectRatio: "1 / 1" }}>
                    <StrokeCanvas script={script} charKey={char.key}
                        animate={true} finishNow={!!answered} onFailed={() => setFailed(true)}
                        onFinished={onAnimationFinished} />
                </div>
              </div>
            : <StrokeCanvas script={script} charKey={char.key}
                animate={true} finishNow={!!answered} onFailed={() => setFailed(true)}
                onFinished={onAnimationFinished} />;

    return (
        <motion.button onClick={onAnswer} disabled={!!answered}
            whileTap={!answered ? { scale: 0.94 } : {}}
            className={`relative flex-1 aspect-square rounded-2xl border-2 overflow-hidden transition-all shadow-sm disabled:cursor-not-allowed ${border}`}>
            <div className="w-full h-full flex items-center justify-center p-[clamp(6px,2vmin,16px)]">
                {char.compound && char.parts
                    ? <CompoundKanaLayout parts={char.parts} script={script}
                        animate={true} finishNow={!!answered} onFailed={() => setFailed(true)}
                        onFinished={onAnimationFinished} />
                    : plainKana
                }
            </div>
            <AnimatePresence>
                {answered && (isCorrect || isSelected) && (
                    <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                        className={`absolute inset-0 flex items-center justify-center ${isCorrect ? "bg-green-100/85" : "bg-red-100/85"}`}>
                        <span className={`font-bold select-none leading-none ${isCorrect ? "text-green-500" : "text-red-500"}`}
                            style={{ fontSize: "clamp(3rem,11vmin,6rem)" }}>
                            {isCorrect ? "○" : "✕"}
                        </span>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.button>
    );
}

// ─── Stars (level selector — text) ───────────────────────────────────────────
function Stars({ count, max = 3 }) {
    return (
        <span className="text-[#b5435f] tracking-tight">
            {"★".repeat(count)}<span className="text-[#b5435f]/25">{"★".repeat(max - count)}</span>
        </span>
    );
}

// ─── ScoreStars (result page — FA icons) ─────────────────────────────────────
function ScoreStars({ score }) {
    const filled = score === 10 ? 3 : score >= 5 ? 2 : score >= 1 ? 1 : 0;
    return (
        <div className="flex items-center justify-center gap-2 my-1">
            {[0, 1, 2].map((i) => (
                i < filled
                    ? <i key={i} className="fa-solid fa-star" style={{ color: "#2797c4", fontSize: "clamp(1.8rem,7vmin,2.6rem)" }} />
                    : <i key={i} className="fa-solid fa-star" style={{ color: "#2797c4", opacity: 0.25, fontSize: "clamp(1.8rem,7vmin,2.6rem)" }} />
            ))}
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
const GAME_PAGE_META = {
    en: {
        title: 'Play "Which Kana Is This?" | Hanapin Japanese Game',
        desc:  "Can you tell apart look-alike hiragana and katakana? Play the listening game and test your kana knowledge!",
    },
    jp: {
        title: "ゲーム「どっちかな？」| はなぴん",
        desc:  "形が似ているひらがなやカタカナ、見分けられるかな？",
    },
    zh: {
        title: "《这是哪个假名？》游戏 | Hanapin",
        desc:  "长得很像的平假名和片假名，你能分得清吗？",
    },
};

export default function HiraganaQuiz({ lang = "en" }) {
    const t = T[lang] ?? T.en;

    const [phase,           setPhase]           = useState("start");
    const [script,          setScript]          = useState("hiragana");
    const [level,           setLevel]           = useState(1);
    const [quizScript,      setQuizScript]      = useState("hiragana"); // locked for result page display
    const [questions,       setQuestions]       = useState([]);
    const [qIdx,            setQIdx]            = useState(0);
    const [answered,        setAnswered]        = useState(null);
    const [replayCount,     setReplayCount]     = useState(0);
    const [results,         setResults]         = useState([]);
    const [prevCorrectKeys, setPrevCorrectKeys] = useState(null);
    const [timeLeft,        setTimeLeft]        = useState(null);
    const [resultRevealed,  setResultRevealed]  = useState(false);
    const [compoundMode,    setCompoundMode]    = useState(false);

    // ── Per-page SEO ──────────────────────────────────────────────────────────
    useEffect(() => {
        const meta = GAME_PAGE_META[lang] || GAME_PAGE_META.en;
        document.title = meta.title;
        const el = document.querySelector('meta[name="description"]');
        if (el) el.setAttribute("content", meta.desc);
    }, [lang]);

    const audioRef          = useRef(null);
    const feedbackAudioRef  = useRef(null);
    const countdownRef      = useRef(null);
    const countdownAudioRef = useRef(null);  // tracks the playing 3-sec countdown audio
    const answeredRef       = useRef(false);
    const quizBgmRef        = useRef(null);
    const resultBgmRef      = useRef(null);
    const resultAudioDoneRef = useRef(false); // prevents double-play in Strict Mode
    // Pre-unlocked Audio element for mobile: browsers (esp. iOS) block new Audio().play()
    // outside a user gesture. We create+activate this element on the Start tap, then
    // reuse it for every char auto-play — including those triggered after a timeout.
    const charAudioElemRef  = useRef(null);
    const feedbackElemRef   = useRef(null); // same pattern for feedback sounds
    const pendingStartTimerRef = useRef(null); // holds startTimer until both animations + audio finish
    const pendingSignalsRef    = useRef(0);    // counts remaining "done" signals before startTimer fires

    const MAX_REPLAYS = level >= 3 ? 1 : level === 2 ? 2 : Infinity;
    const currentQ    = questions[qIdx] ?? null;
    const answerChar  = currentQ ? currentQ.chars[currentQ.answerIdx] : null;
    const score       = results.filter((r) => r.correct).length;
    const wrongResults = results.filter((r) => !r.correct);

    // ── Audio helpers ────────────────────────────────────────────────────────
    const playCharAudio = (key) => {
        const url = CHAR_AUDIO[key];
        if (!url) return null;
        try { audioRef.current?.pause(); } catch (_) {}

        // Reuse the pre-unlocked element so mobile can play after timeouts.
        // iOS blocks new Audio().play() outside a user gesture, but a previously
        // "activated" element can change src and play freely.
        const pre = charAudioElemRef.current;
        if (pre) {
            pre.onended = null;
            try { pre.pause(); } catch (_) {}
            pre.src = url;
            pre.currentTime = 0;
            pre.volume = 1;
            pre.play().catch(() => {});
            audioRef.current = pre;
            return pre;
        }

        // Fallback: new element (desktop always works; mobile works if called during gesture)
        const a = new Audio(url);
        audioRef.current = a;
        a.play().catch(() => {});
        return a;
    };

    // Play feedback (correct / wrong) via a pre-unlocked element for mobile
    const playFeedbackAudio = (url) => {
        if (!url) return;
        try { feedbackAudioRef.current?.pause(); } catch (_) {}
        const pre = feedbackElemRef.current;
        if (pre) {
            pre.onended = null;
            try { pre.pause(); } catch (_) {}
            pre.src = url;
            pre.currentTime = 0;
            pre.volume = 1;
            pre.play().catch(() => {});
            feedbackAudioRef.current = pre;
            return;
        }
        const a = new Audio(url);
        feedbackAudioRef.current = a;
        a.play().catch(() => {});
    };

    const playTap = () => { if (TAP_AUDIO) new Audio(TAP_AUDIO).play().catch(() => { }); };

    const stopCountdown = () => {
        if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
        // Also stop any playing countdown audio (e.g. user answered before timer hit 0)
        if (countdownAudioRef.current) {
            try { countdownAudioRef.current.pause(); } catch (_) { }
            countdownAudioRef.current = null;
        }
    };

    // ── BGM: quiz phase ──────────────────────────────────────────────────────
    useEffect(() => {
        if (phase !== "quiz") {
            if (quizBgmRef.current) { quizBgmRef.current.pause(); quizBgmRef.current = null; }
            return;
        }
        if (!QUIZ_BGM) return;
        const bgm = new Audio(QUIZ_BGM);
        bgm.loop = true; bgm.volume = 0.4;
        bgm.play().catch(() => { });
        quizBgmRef.current = bgm;
        return () => { bgm.pause(); };
    }, [phase]);

    // ── Good job overlay → then reveal result + BGM ──────────────────────────
    useEffect(() => {
        if (phase !== "result") {
            if (resultBgmRef.current) { resultBgmRef.current.pause(); resultBgmRef.current = null; }
            resultAudioDoneRef.current = false;
            return;
        }
        // Guard against double-invocation (React Strict Mode)
        if (resultAudioDoneRef.current) return;
        resultAudioDoneRef.current = true;

        let t1, t2;
        const doReveal = () => {
            setResultRevealed(true);
            if (RESULT_BGM && !resultBgmRef.current) {
                const bgm = new Audio(RESULT_BGM);
                bgm.loop = true; bgm.volume = 0.4;
                bgm.play().catch(() => { });
                resultBgmRef.current = bgm;
            }
        };

        const gjUrl = GOODJOB_AUDIO[lang] || GOODJOB_AUDIO.en;
        if (gjUrl) {
            const gj = new Audio(gjUrl);
            gj.onended = doReveal;
            gj.play().catch(() => { t1 = setTimeout(doReveal, 3000); });
            t2 = setTimeout(doReveal, 12000); // absolute failsafe
        } else {
            t1 = setTimeout(doReveal, 800);
        }

        return () => {
            clearTimeout(t1); clearTimeout(t2);
            if (resultBgmRef.current) { resultBgmRef.current.pause(); resultBgmRef.current = null; }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, lang]);

    // ── Question load: auto-play + start countdown after audio finishes ──────
    useEffect(() => {
        if (phase !== "quiz" || !currentQ) return;
        answeredRef.current = false;
        setReplayCount(0);
        stopCountdown();
        try { feedbackAudioRef.current?.pause(); feedbackAudioRef.current = null; } catch (_) {}
        setTimeLeft(null); // hide timer while audio plays

        const charAudio = playCharAudio(currentQ.chars[currentQ.answerIdx].key);

        const startTimer = () => {
            const secs = COUNTDOWN_SECS[level];
            setTimeLeft(secs);
            let remaining = secs;
            let countdownStarted = false;

            const playCountdown = () => {
                if (countdownStarted || !COUNTDOWN_END) return;
                countdownStarted = true;
                const cdAudio = new Audio(COUNTDOWN_END);
                countdownAudioRef.current = cdAudio;
                cdAudio.play().catch(() => { countdownAudioRef.current = null; });
                cdAudio.onended = () => { countdownAudioRef.current = null; };
            };

            // If total time is already ≤ 3s, play countdown immediately
            if (remaining <= 3) playCountdown();

            countdownRef.current = setInterval(() => {
                remaining--;
                if (COUNTING_AUDIO && remaining > 3) new Audio(COUNTING_AUDIO).play().catch(() => { });
                if (remaining <= 3) playCountdown();
                if (remaining <= 0) { stopCountdown(); setTimeLeft(0); }
                else setTimeLeft(remaining);
            }, 1000);
        };

        // Wait for BOTH character animations + audio (if any) before starting countdown.
        // Each source fires signalDone(); when the counter hits 0, startTimer runs.
        pendingStartTimerRef.current = startTimer;
        pendingSignalsRef.current = charAudio ? 3 : 2; // 2 animations + optional audio

        const signalDone = () => {
            pendingSignalsRef.current = Math.max(0, pendingSignalsRef.current - 1);
            if (pendingSignalsRef.current === 0 && pendingStartTimerRef.current) {
                const fn = pendingStartTimerRef.current;
                pendingStartTimerRef.current = null;
                fn();
            }
        };

        if (charAudio) {
            const audioFailsafe = setTimeout(signalDone, 4000);
            charAudio.onended = () => { clearTimeout(audioFailsafe); signalDone(); };
        }

        // Absolute failsafe: fire regardless if animations never complete (SVG load failure etc.)
        const animFailsafe = setTimeout(() => {
            if (pendingStartTimerRef.current) {
                pendingStartTimerRef.current();
                pendingStartTimerRef.current = null;
            }
        }, 8000);
        return () => { stopCountdown(); clearTimeout(animFailsafe); pendingStartTimerRef.current = null; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qIdx, phase, level, compoundMode]);

    // ── Auto-advance when countdown hits 0 ───────────────────────────────────
    useEffect(() => {
        if (timeLeft === 0 && phase === "quiz" && !answeredRef.current) handleTimeout();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeLeft]);

    // ── Shared advance ───────────────────────────────────────────────────────
    const doAdvance = (newResults) => {
        try { feedbackAudioRef.current?.pause(); feedbackAudioRef.current = null; } catch (_) {}
        setAnswered(null);
        if (qIdx + 1 >= 10) {
            // Stop quiz BGM immediately — don't let it bleed into the result screen
            if (quizBgmRef.current) { quizBgmRef.current.pause(); quizBgmRef.current = null; }
            const correctKeys = new Set(newResults.filter((r) => r.correct).map((r) => pairKey(r.q.pair)));
            setPrevCorrectKeys(correctKeys);
            // Reset BEFORE phase change so they batch into one render —
            // this guarantees the goodjob overlay shows on every quiz, not just the first.
            setResultRevealed(false);
            setPhase("result");
        } else {
            setQIdx((i) => i + 1);
        }
    };

    // ── User answer: char sound → then feedback ──────────────────────────────
    const handleAnswer = (idx) => {
        if (answered || !currentQ || answeredRef.current) return;
        answeredRef.current = true;
        stopCountdown(); // also stops any playing countdown audio
        const correct    = idx === currentQ.answerIdx;
        const newResults = [...results, { correct, q: currentQ }];
        setAnswered({ selectedIdx: idx });
        setResults(newResults);

        const charAudioObj = playCharAudio(currentQ.chars[idx].key);
        const fbUrl = correct ? FEEDBACK.correct : FEEDBACK.wrong;
        const playFeedback = () => { playFeedbackAudio(fbUrl); };
        if (charAudioObj) charAudioObj.onended = playFeedback;
        else playFeedback();

        setTimeout(() => doAdvance(newResults), 1500);
    };

    // ── Timeout: wait for the countdown audio (already playing) to finish ────
    const handleTimeout = () => {
        if (answeredRef.current || !currentQ) return;
        answeredRef.current = true;
        const newResults = [...results, { correct: false, q: currentQ }];
        setAnswered({ selectedIdx: -1 });
        setResults(newResults);
        playFeedbackAudio(FEEDBACK.wrong);

        const cdAudio = countdownAudioRef.current;
        if (cdAudio) {
            let done = false;
            const finish = () => { if (!done) { done = true; doAdvance(newResults); } };
            cdAudio.onended = finish;
            setTimeout(finish, 4000); // failsafe
        } else {
            setTimeout(() => doAdvance(newResults), 1000);
        }
    };

    // ── Start quiz ───────────────────────────────────────────────────────────
    const startQuiz = (chosenScript, chosenLevel, skipKeys = null) => {
        // Unlock persistent Audio elements during this user-gesture context.
        // On iOS Safari, calling play() on an element during a gesture "activates"
        // it — after that, the same element can play programmatically (e.g. after
        // a timeout) without needing another gesture.
        const unlockElem = (ref) => {
            if (!ref.current) ref.current = new Audio();
            const a = ref.current;
            a.volume = 0;
            // Use tap audio as unlock src so iOS has a valid media resource
            if (TAP_AUDIO) a.src = TAP_AUDIO;
            a.play().catch(() => {});
            // Don't pause immediately — let it complete silently (tap is ~0.1s)
        };
        unlockElem(charAudioElemRef);
        unlockElem(feedbackElemRef);

        stopCountdown();
        const qs = generateQuestions(chosenScript, chosenLevel, skipKeys);
        setScript(chosenScript); setLevel(chosenLevel);
        setQuizScript(chosenScript); // lock script for result page SVGs
        setCompoundMode(false);
        setQuestions(qs); setQIdx(0);
        setResults([]); setAnswered(null); setReplayCount(0); setTimeLeft(null);
        setPhase("quiz");
    };

    const startCompoundQuiz = (chosenScript) => {
        const unlockElem = (ref) => {
            if (!ref.current) ref.current = new Audio();
            const a = ref.current;
            a.volume = 0;
            if (TAP_AUDIO) a.src = TAP_AUDIO;
            a.play().catch(() => {});
        };
        unlockElem(charAudioElemRef);
        unlockElem(feedbackElemRef);

        stopCountdown();
        const qs = generateCompoundQuestions(chosenScript);
        setScript(chosenScript);
        setLevel(2); // 4 seconds per question
        setQuizScript(chosenScript);
        setCompoundMode(true);
        setQuestions(qs); setQIdx(0);
        setResults([]); setAnswered(null); setReplayCount(0); setTimeLeft(null);
        setPhase("quiz");
    };

    const handleReplay = () => {
        if (!answerChar || answered || replayCount >= MAX_REPLAYS) return;
        setReplayCount((c) => c + 1);
        playCharAudio(answerChar.key);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // ── START SCREEN ─────────────────────────────────────────────────────────
    // ─────────────────────────────────────────────────────────────────────────
    if (phase === "start") {
        return (
            <section className="h-[100dvh] flex flex-col items-center justify-center overflow-hidden
                                bg-[#fffaf0] pt-[60px] px-6 gap-6">
                <div className="text-center">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">{t.title}</h1>
                    <p className="text-sm text-gray-400 mt-1">{t.subtitle}</p>
                </div>
                <div className="flex border-2 border-[#fdcb6e] rounded-full overflow-hidden">
                    {["hiragana", "katakana"].map((s) => (
                        <button key={s} onClick={() => { playTap(); setScript(s); }}
                            className={`px-7 py-2 text-sm font-semibold transition
                                ${script === s ? "bg-[#fdcb6e] text-gray-900" : "text-gray-500 hover:bg-[#fdcb6e]/20"}`}>
                            {s === "hiragana" ? t.hiragana : t.katakana}
                        </button>
                    ))}
                </div>
                <div className="flex flex-col gap-3 w-full max-w-xs">
                    {[
                        { lv: 1, desc: t.easy,   secs: COUNTDOWN_SECS[1] },
                        { lv: 2, desc: t.mixed,  secs: COUNTDOWN_SECS[2] },
                        { lv: 3, desc: t.tricky, secs: COUNTDOWN_SECS[3] },
                    ].map(({ lv, desc, secs }) => (
                        <motion.button key={lv} whileTap={{ scale: 0.96 }}
                            onClick={() => { playTap(); startQuiz(script, lv, null); }}
                            className="flex items-center justify-between px-5 py-3.5 rounded-2xl border-2
                                       border-[#b5435f] bg-white hover:border-[#2797c4] hover:border-[3px] transition-all shadow-sm">
                            <div className="text-left">
                                <p className="text-sm font-bold text-gray-800">{t.level} {lv}</p>
                                <p className="text-xs text-gray-400">{desc} · {secs}s</p>
                            </div>
                            <Stars count={lv} />
                        </motion.button>
                    ))}
                </div>
            </section>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // ── QUIZ SCREEN ───────────────────────────────────────────────────────────
    // ─────────────────────────────────────────────────────────────────────────
    if (phase === "quiz" && currentQ) {
        const canReplay   = !answered && replayCount < MAX_REPLAYS;
        const cdTotal     = COUNTDOWN_SECS[level];
        const anyCompound = currentQ.chars.some(c => c.compound);
        return (
            <section className="h-[100dvh] flex flex-col overflow-hidden bg-[#fffaf0] pt-[60px]">
                <div className="flex-shrink-0 h-1 bg-gray-100">
                    <motion.div className="h-full bg-[#2797c4]"
                        animate={{ width: `${(qIdx / 10) * 100}%` }} transition={{ duration: 0.35 }} />
                </div>
                <div className="flex-1 min-h-0 flex flex-col items-center justify-evenly px-5 py-2 gap-1">

                    {/* Header */}
                    <div className="w-full max-w-md flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => { playTap(); stopCountdown(); setPhase("start"); }}
                                className="text-gray-400 hover:text-gray-600 transition"
                                style={{ fontSize: "clamp(1rem,4.5vmin,1.25rem)", lineHeight: 1 }}>
                                <i className="fa-solid fa-right-from-bracket" style={{ display: "inline-block", transform: "scaleX(-1)" }} />
                            </button>
                            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                {script === "hiragana" ? t.hiragana : t.katakana} · {compoundMode ? t.compoundLabel : <Stars count={level} />}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            {timeLeft !== null && !answered && (
                                <CountdownRing key={qIdx} timeLeft={timeLeft} total={cdTotal} />
                            )}
                            <span className="text-sm font-bold text-[#2797c4]">
                                {qIdx + 1}<span className="font-normal text-gray-400"> / 10</span>
                            </span>
                        </div>
                    </div>

                    {/* Replay */}
                    <div className="flex flex-col items-center gap-1">
                        <motion.button onClick={handleReplay} disabled={!canReplay}
                            whileTap={canReplay ? { scale: 0.88 } : {}}
                            className={`rounded-full flex items-center justify-center shadow-md border-2 transition-all
                                ${canReplay
                                    ? "bg-white border-[#b5435f] hover:border-[#2797c4] hover:border-[3px]"
                                    : "bg-gray-100 border-gray-200 opacity-40 cursor-not-allowed"}`}
                            style={{ width: "clamp(52px,13vmin,72px)", height: "clamp(52px,13vmin,72px)", fontSize: "clamp(1.4rem,5vmin,2rem)" }}>
                            <Headphones size={28} style={{ display: "block" }} />
                        </motion.button>
                        {level === 3 && (
                            <span className="text-[10px] text-gray-400">
                                {t.replaysLeft(Math.max(0, MAX_REPLAYS - replayCount))}
                            </span>
                        )}
                        <p className="text-xs text-gray-400">{t.tapToHear}</p>
                    </div>

                    {/* Answer buttons */}
                    <div className="flex gap-3 sm:gap-5 w-full max-w-md">
                        {currentQ.chars.map((char, idx) => (
                            <CharButton key={`${qIdx}-${idx}`} char={char} script={script}
                                isCorrect={idx === currentQ.answerIdx}
                                answered={answered}
                                isSelected={answered?.selectedIdx === idx}
                                onAnswer={() => handleAnswer(idx)}
                                sizedAsCompound={anyCompound && !char.compound}
                                onAnimationFinished={() => {
                                    pendingSignalsRef.current = Math.max(0, pendingSignalsRef.current - 1);
                                    if (pendingSignalsRef.current === 0 && pendingStartTimerRef.current) {
                                        const fn = pendingStartTimerRef.current;
                                        pendingStartTimerRef.current = null;
                                        fn?.();
                                    }
                                }} />
                        ))}
                    </div>

                    {/* Progress dots */}
                    <div className="flex gap-1.5 items-center">
                        {Array.from({ length: 10 }).map((_, i) => {
                            const r = results[i];
                            return (
                                <div key={i} className={`rounded-full transition-all duration-300
                                    ${i === qIdx  ? "w-3 h-3 bg-[#2797c4]"
                                    : r?.correct  ? "w-2 h-2 bg-green-500"
                                    : r           ? "w-2 h-2 bg-red-500"
                                    :               "w-2 h-2 bg-gray-200"}`} />
                            );
                        })}
                    </div>
                </div>
            </section>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // ── RESULT SCREEN + GOOD JOB OVERLAY ────────────────────────────────────
    // Result screen renders behind the goodjob overlay.
    // Hanamaru starts only after resultRevealed (goodjob audio done).
    // ─────────────────────────────────────────────────────────────────────────
    if (phase === "result") {
    const message = score === 10 ? t.perfect : score >= 7 ? t.great : score >= 4 ? t.keepGoing : t.tryAgain;

    return (
        <>
        <AnimatePresence>
        {resultRevealed && (
        <motion.section
            className="min-h-[100dvh] flex flex-col items-center overflow-y-auto bg-[#fffaf0] pt-[60px] px-5 py-6 gap-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.15 }}>

            {/* Score */}
            <div className="text-center flex-shrink-0">
                {/* <ScoreStars score={score} /> */}
                <p className="text-4xl font-bold text-[#b5435f]">
                    {score}<span className="text-xl text-gray-400 font-normal"> {t.outOf}</span>
                </p>
                <p className="text-sm text-gray-500 mt-0.5">{message}</p>
                {/* Hanamaru video for perfect 10/10 — starts after goodjob audio finishes */}
                {score === 10 && HANAMARU_VIDEO && <HanamaruCanvas videoSrc={HANAMARU_VIDEO} play={resultRevealed} />}
            </div>

            {/* Wrong answer review — pairs grouped, locked to quizScript */}
            {wrongResults.length > 0 ? (
                <div className="w-full max-w-lg flex-shrink-0">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2 text-center">
                        {t.review}
                    </p>
                    <div className="flex flex-wrap gap-x-6 gap-y-4 justify-center">
                        {wrongResults.map(({ q }, qi) => {
                            const anyComp = q.chars.some(c => c.compound);
                            return (
                            <div key={qi} className="flex gap-2">
                                {q.chars.map((c, ci) => {
                                    const isAnswer = ci === q.answerIdx;
                                    const sizedAsComp = anyComp && !c.compound;
                                    return (
                                        <motion.button
                                            key={`${qi}-${ci}`}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() => { playCharAudio(c.key); }}
                                            className="flex flex-col items-center gap-1.5 bg-[#fff] rounded-xl p-2
                                                       border border-gray-100 shadow-sm cursor-pointer
                                                       hover:border-[#2797c4] hover:border-2 transition-colors"
                                            style={{ width: "clamp(80px, 20vw, 120px)" }}>
                                            <span className={`text-[12px] font-semibold tracking-wide
                                                ${isAnswer ? "text-green-600" : "text-gray-400"}`}>
                                                {c.romaji ?? c.key}
                                            </span>
                                            <div className={`rounded-lg overflow-hidden w-full aspect-square p-1
                                                ${isAnswer
                                                    ? "ring-2 ring-green-400 bg-[#fff]"
                                                    : "ring-1 ring-gray-200 bg-[#fff] opacity-50"}`}>
                                                {c.compound && c.parts
                                                    ? <CompoundKanaLayout
                                                        parts={c.parts}
                                                        script={quizScript}
                                                        animate={false}
                                                        strokeColor={isAnswer ? "#16a34a" : "#6b7280"}
                                                        finishNow={false}
                                                      />
                                                    : sizedAsComp
                                                        ? <div className="w-full h-full flex items-center justify-center">
                                                            <div style={{ width: "62.5%", aspectRatio: "1/1" }}>
                                                                <StrokeCanvas
                                                                    script={quizScript}
                                                                    charKey={c.key}
                                                                    animate={false}
                                                                    strokeColor={isAnswer ? "#16a34a" : "#6b7280"}
                                                                />
                                                            </div>
                                                          </div>
                                                        : <StrokeCanvas
                                                            script={quizScript}
                                                            charKey={c.key}
                                                            animate={false}
                                                            strokeColor={isAnswer ? "#16a34a" : "#6b7280"}
                                                          />
                                                }
                                            </div>
                                        </motion.button>
                                    );
                                })}
                            </div>
                            );
                        })}
                    </div>
                </div>
            ) : (
                <div className="text-center py-2 flex-shrink-0">
                    <p className="text-sm text-gray-500 mt-1">{t.noMistakes}</p>
                </div>
            )}

            {/* Controls */}
            <div className="flex flex-col items-center gap-2.5 w-full max-w-xs flex-shrink-0">
                <div className="flex border-2 border-[#fdcb6e] rounded-full overflow-hidden w-full">
                    {["hiragana", "katakana"].map((s) => (
                        <button key={s} onClick={() => { playTap(); setScript(s); }}
                            className={`flex-1 py-1.5 text-sm font-semibold transition
                                ${script === s ? "bg-[#fdcb6e] text-gray-900" : "text-gray-500 hover:bg-[#fdcb6e]/20"}`}>
                            {s === "hiragana" ? t.hiragana : t.katakana}
                        </button>
                    ))}
                </div>
                <div className="flex border-2 border-[#b5435f]/50 rounded-full overflow-hidden w-full">
                    {[1, 2, 3].map((lv) => (
                        <button key={lv} onClick={() => { playTap(); setLevel(lv); }}
                            className={`flex-1 py-1.5 text-sm font-semibold transition
                                ${level === lv ? "bg-[#b5435f] text-white" : "text-gray-500 hover:bg-[#b5435f]/5"}`}>
                            {"★".repeat(lv)}
                        </button>
                    ))}
                </div>
                <motion.button whileTap={{ scale: 0.96 }}
                    onClick={() => { playTap(); startQuiz(script, level, prevCorrectKeys); }}
                    className="w-full py-3 rounded-2xl bg-[#b5435f] text-white font-bold text-sm
                               hover:bg-[#9e3a52] active:scale-95 transition-all shadow-md">
                    {t.playAgain}
                </motion.button>
                <button onClick={() => { playTap(); setPrevCorrectKeys(null); setPhase("start"); }}
                    className="text-xs text-gray-400 hover:text-gray-600 transition">
                    {t.backToMenu}
                </button>
            </div>
        </motion.section>
        )}
        </AnimatePresence>
        <AnimatePresence>
            {!resultRevealed && (
                <motion.section
                    className="fixed inset-0 flex flex-col items-center justify-center gap-6 bg-[#fffaf0] z-50"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}>
                    <motion.p
                        className="text-[clamp(2rem,10vmin,4rem)] font-bold text-[#b5435f] text-center px-8 leading-tight"
                        initial={{ scale: 0.4, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 180, damping: 14, delay: 0.1 }}>
                        {t.goodjob}
                    </motion.p>
                    <motion.div
                        className="flex gap-1.5"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.4 }}>
                        {Array.from({ length: 10 }).map((_, i) => (
                            <div key={i} className={`w-2 h-2 rounded-full ${results[i]?.correct ? "bg-green-500" : results[i] ? "bg-red-400" : "bg-gray-200"}`} />
                        ))}
                    </motion.div>
                </motion.section>
            )}
        </AnimatePresence>
        </>
    );
    }
}
